/**
 * packageValidation.js (WP-5)
 *
 * §3.9 self-containment validation, enforced at Save as Package (and on .sampkg
 * import). A package must be fully self-contained: nothing inside it may depend
 * on, or leak into, anything outside it. Enforcing this at the source keeps every
 * downstream stage simple — expansion never needs host-context repair, and a
 * package that validates will expand correctly into any host.
 *
 * validatePackageStructure(nodes, edges) → { isValid, errors[], warnings[], notices[] }
 *   errors   block Save as Package.
 *   warnings surface but do not block.
 *   notices  are informational (i) statements — never block, never a warning.
 */

import { validateMediaPairs } from './validation';

// Same boundary convention as variableScanner.js: a token boundary is the start
// or end of the string, or any char in [^A-Za-z0-9_-].
function escapeReg(s = '') {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Reserved words that may appear in a condition but are not variable references.
const CONDITION_KEYWORDS = new Set([
  'and', 'or', 'not', 'true', 'false', 'null', 'in', 'contains', 'matches',
  'startswith', 'endswith', 'length', 'is', 'empty',
]);

const isInputType = (n) => ['text', 'number', 'choice', 'yes_no', 'scale', 'question'].includes(n.type);

/** BFS reachable set from a start node over directed edges. */
function reachableFrom(startId, edges) {
  const reachable = new Set();
  const queue = [startId];
  while (queue.length) {
    const cur = queue.shift();
    if (reachable.has(cur)) continue;
    reachable.add(cur);
    edges.filter((e) => e.source === cur).forEach((e) => {
      if (!reachable.has(e.target)) queue.push(e.target);
    });
  }
  return reachable;
}

/** Collect variable names defined by the package's own input questions. */
function collectDefinedVariables(nodes) {
  const defined = new Set();
  nodes.forEach((n) => {
    const d = n.data || {};
    const v = d.variable || d.variable_name;
    if (v && String(v).trim() !== '' && isInputType(n)) defined.add(String(v).trim());
    // System-produced variables that count as internal for a self-contained package.
    if (d.type === 'file') defined.add('File_Upload_Path');
  });
  return defined;
}

/** Extract candidate variable identifiers referenced by a condition string. */
function referencedIdentifiers(condition) {
  if (typeof condition !== 'string' || condition.trim() === '') return [];
  // Strip quoted string literals so quoted values aren't treated as variables.
  const stripped = condition.replace(/(['"][^'"\\]*(?:\\.[^'"\\]*)*['"])/g, ' ');
  const out = [];
  const re = /[A-Za-z_][A-Za-z0-9_]*/g;
  let m;
  while ((m = re.exec(stripped)) !== null) {
    const tok = m[0];
    if (CONDITION_KEYWORDS.has(tok.toLowerCase())) continue;
    out.push(tok);
  }
  return out;
}

export function validatePackageStructure(nodes = [], edges = []) {
  const errors = [];
  const warnings = [];
  const notices = [];

  const startNodes = nodes.filter((n) => n.type === 'start');
  const endNodes = nodes.filter((n) => n.type === 'end');

  // 1. Exactly one Start and one End.
  if (startNodes.length === 0) errors.push('Package must have exactly one Start node.');
  else if (startNodes.length > 1) errors.push('Package cannot have multiple Start nodes.');
  if (endNodes.length === 0) errors.push('Package must have exactly one End node.');
  else if (endNodes.length > 1) errors.push('Package cannot have multiple End nodes.');

  // 2. No nested package nodes (v1 no-nesting rule).
  if (nodes.some((n) => n.type === 'package')) {
    errors.push('Package cannot contain another package (nesting is not supported).');
  }

  // 3. Unique variable names among input questions.
  const inputVars = nodes
    .filter((n) => isInputType(n) && (n.data?.variable || n.data?.variable_name))
    .map((n) => String(n.data.variable || n.data.variable_name).trim())
    .filter((v) => v !== '');
  const dupVars = inputVars.filter((v, i) => inputVars.indexOf(v) !== i);
  if (dupVars.length > 0) {
    errors.push(`Duplicate variable names: ${[...new Set(dupVars)].join(', ')}.`);
  }
  // Questions without a variable — a warning (existing behavior).
  const noVar = nodes.filter((n) => isInputType(n) && !(n.data?.variable || n.data?.variable_name));
  if (noVar.length > 0) {
    warnings.push(`${noVar.length} question(s) have no variable name.`);
  }

  // 4/5. Reachability: unreachable nodes are ERRORS (review A9); End must be reachable.
  if (startNodes.length === 1 && endNodes.length === 1) {
    const reachable = reachableFrom(startNodes[0].id, edges);
    const unreachable = nodes.filter((n) => n.id !== startNodes[0].id && !reachable.has(n.id));
    if (unreachable.length > 0) {
      const ids = unreachable.map((n) => n.data?.id || n.id);
      errors.push(`Unreachable node(s) from Start: ${ids.join(', ')}.`);
    }
    if (!reachable.has(endNodes[0].id)) {
      errors.push('End node is not reachable from Start.');
    }
  }

  // 6. Edges must be fully internal (both endpoints exist inside the package).
  const nodeIds = new Set(nodes.map((n) => n.id));
  edges.forEach((e) => {
    if (!nodeIds.has(e.source) || !nodeIds.has(e.target)) {
      errors.push(`Edge references a node outside the package (${e.source} → ${e.target}).`);
    }
  });

  // 7. Media: fully resolved + paired (no open stimulus at exit, no orphan dismiss).
  const mediaNodes = nodes.filter((n) => n.type === 'media' || n.data?.type === 'media');
  mediaNodes.forEach((n) => {
    const d = n.data || {};
    const isDisplay = d.show !== false;
    const label = d.id || n.id;
    if (isDisplay) {
      const src = String(d.media?.src || '').trim();
      const assetId = d.media?.asset_id;
      if (!src) errors.push(`Media "${label}" has no source (media.src).`);
      if (!assetId) errors.push(`Media "${label}" references an unsaved asset (asset_id missing).`);
    }
  });
  // Reuse the graph media-pair validator: open stimuli reaching End, show without hide.
  validateMediaPairs(nodes, edges).forEach((issue) => {
    errors.push(issue.message);
  });
  // Every dismiss's stimulus must be displayed inside the package.
  const displayedStimuli = new Set(
    mediaNodes.filter((n) => (n.data?.show !== false) && n.data?.stimulus_id).map((n) => String(n.data.stimulus_id).trim())
  );
  mediaNodes.filter((n) => n.data?.show === false).forEach((n) => {
    const s = String(n.data?.stimulus_id || '').trim();
    if (s && !displayedStimuli.has(s)) {
      errors.push(`Dismiss of stimulus "${s}" has no matching display inside the package.`);
    }
  });

  // 8. Conditions reference only internal variables.
  const definedVars = collectDefinedVariables(nodes);
  edges.forEach((e) => {
    const cond = e.data?.condition;
    referencedIdentifiers(cond).forEach((name) => {
      if (!definedVars.has(name)) {
        errors.push(`Condition references "${name}", which is not collected inside the package.`);
      }
    });
  });

  // 9. Start/End text is ignored at embed time → informational notice, never a warning.
  [...startNodes, ...endNodes].forEach((n) => {
    const text = String(n.data?.text || n.data?.question_text || '').trim();
    if (text) {
      const which = n.type === 'start' ? 'Start' : 'End';
      notices.push(`${which} node text is not delivered when the package is embedded (it plays only in standalone testing).`);
    }
  });

  // Dedupe messages (media-pair + our own checks can overlap).
  const uniq = (arr) => [...new Set(arr)];
  return {
    isValid: errors.length === 0,
    errors: uniq(errors),
    warnings: uniq(warnings),
    notices: uniq(notices),
  };
}

export default validatePackageStructure;
